import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import MandatePreviewPanel from '../../components/mandates/MandatePreviewPanel';
import { MandateTimelineCompact } from '../../components/mandates/MandateTimeline';
import {
    FileText, Plus, Search, Download, CheckCircle, Send,
    FileCheck, Clock, MapPin, ChevronRight, ChevronDown, ChevronUp,
    SlidersHorizontal, X, ArrowUpDown, Home, Euro, Calendar, Filter, Upload
} from 'lucide-react';

// Status configuration
const STATUS_CONFIG = {
    all: { label: 'Tous', shortLabel: 'Tous', color: 'bg-white/10 text-white', icon: FileText },
    draft: { label: 'Brouillon', shortLabel: 'Brouillon', color: 'bg-slate-500/20 text-slate-400', icon: Clock },
    generated: { label: 'PDF généré', shortLabel: 'PDF', color: 'bg-blue-500/20 text-blue-400', icon: FileCheck },
    sent: { label: 'Envoyé', shortLabel: 'Envoyé', color: 'bg-amber-500/20 text-amber-400', icon: Send },
    acknowledged: { label: 'Validé', shortLabel: 'Validé', color: 'bg-green-500/20 text-green-400', icon: CheckCircle }
};

const TYPE_LABELS = { simple: 'Simple', exclusif: 'Exclusif', semi_exclusif: 'Semi-excl.' };
const TYPE_COLORS = {
    simple: 'border-slate-500/30 text-slate-400',
    exclusif: 'border-[#FF914D]/30 text-[#FF914D]',
    semi_exclusif: 'border-[#681793]/30 text-[#681793]'
};

const fadeIn = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.04 } } };

const formatCurrencyShort = (amount) => {
    if (!amount) return '-';
    if (amount >= 1000000) return `${(amount / 1000000).toFixed(1)}M €`;
    if (amount >= 1000) return `${Math.round(amount / 1000)}k €`;
    return `${amount} €`;
};

const formatCurrencyFull = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(amount);
};

const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try { return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }); }
    catch { return dateStr; }
};

const formatDateLong = (dateStr) => {
    if (!dateStr) return '';
    try { return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return dateStr; }
};

function MandatesPage() {
    const { isAdmin, isAgent, user } = useAuth();
    const [searchParams] = useSearchParams();
    const [mandates, setMandates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
    const [searchOpen, setSearchOpen] = useState(false);
    const [previewId, setPreviewId] = useState(null);
    
    // Advanced filters (desktop)
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
    const [typeFilter, setTypeFilter] = useState('all');
    const [cityFilter, setCityFilter] = useState('');
    const [priceMin, setPriceMin] = useState('');
    const [priceMax, setPriceMax] = useState('');
    
    // Sorting
    const [sortBy, setSortBy] = useState('created_at');
    const [sortDir, setSortDir] = useState('desc');

    // Discrete "just synchronised" badge: shown for 3 s when arriving here
    // right after a successful mandate upload (session flag set by
    // MandateUploadPage). Reassures the user the list is up-to-date.
    const [showSyncBadge, setShowSyncBadge] = useState(() => {
        try {
            const ts = Number(sessionStorage.getItem('mandates_just_updated') || 0);
            return ts && (Date.now() - ts) < 30000;
        } catch (e) {
            return false;
        }
    });
    useEffect(() => {
        if (!showSyncBadge) return;
        try { sessionStorage.removeItem('mandates_just_updated'); } catch (e) { /* ignore */ }
        const t = setTimeout(() => setShowSyncBadge(false), 3000);
        return () => clearTimeout(t);
    }, [showSyncBadge]);

    useEffect(() => {
        const loadMandates = async () => {
            try {
                const response = await api.get('/mandates-v2/');
                setMandates(response.data.mandates || []);
            } catch (err) {
                console.error('Error loading mandates:', err);
                toast.error('Erreur lors du chargement des mandats');
            } finally {
                setLoading(false);
            }
        };
        loadMandates();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Extract unique cities for filter
    const cities = useMemo(() => {
        const citySet = new Set(mandates.map(m => m.property?.city).filter(Boolean));
        return Array.from(citySet).sort();
    }, [mandates]);

    // Status counts
    const statusCounts = useMemo(() => ({
        all: mandates.length,
        draft: mandates.filter(m => m.status === 'draft').length,
        generated: mandates.filter(m => m.status === 'generated').length,
        sent: mandates.filter(m => m.status === 'sent').length,
        acknowledged: mandates.filter(m => ['acknowledged', 'active', 'actif'].includes(m.status)).length,
    }), [mandates]);

    // Active advanced filter count
    const advancedFilterCount = [
        typeFilter !== 'all' ? 1 : 0,
        cityFilter ? 1 : 0,
        priceMin ? 1 : 0,
        priceMax ? 1 : 0,
    ].reduce((a, b) => a + b, 0);

    // Filtered & sorted mandates
    const filteredMandates = useMemo(() => {
        let result = mandates.filter(mandate => {
            const sellerNames = mandate.sellers?.map(s => `${s.first_name} ${s.last_name}`).join(' ') || '';
            const matchesSearch = !search || 
                mandate.mandate_number?.toLowerCase().includes(search.toLowerCase()) ||
                sellerNames.toLowerCase().includes(search.toLowerCase()) ||
                mandate.property?.city?.toLowerCase().includes(search.toLowerCase()) ||
                mandate.property?.address?.toLowerCase().includes(search.toLowerCase());
            const matchesStatus = statusFilter === 'all' || mandate.status === statusFilter || 
                (statusFilter === 'acknowledged' && ['acknowledged', 'active', 'actif'].includes(mandate.status));
            const matchesType = typeFilter === 'all' || mandate.mandate_type === typeFilter;
            const matchesCity = !cityFilter || mandate.property?.city === cityFilter;
            const price = mandate.pricing?.sale_price || 0;
            const matchesPriceMin = !priceMin || price >= Number(priceMin);
            const matchesPriceMax = !priceMax || price <= Number(priceMax);
            return matchesSearch && matchesStatus && matchesType && matchesCity && matchesPriceMin && matchesPriceMax;
        });

        // Sort
        result.sort((a, b) => {
            let valA, valB;
            switch (sortBy) {
                case 'price': valA = a.pricing?.sale_price || 0; valB = b.pricing?.sale_price || 0; break;
                case 'mandate_number': valA = a.mandate_number || ''; valB = b.mandate_number || ''; break;
                case 'city': valA = a.property?.city || ''; valB = b.property?.city || ''; break;
                case 'type': valA = a.mandate_type || ''; valB = b.mandate_type || ''; break;
                case 'status': valA = a.status || ''; valB = b.status || ''; break;
                default: valA = a.created_at || ''; valB = b.created_at || '';
            }
            if (typeof valA === 'string') {
                return sortDir === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
            }
            return sortDir === 'asc' ? valA - valB : valB - valA;
        });

        return result;
    }, [mandates, search, statusFilter, typeFilter, cityFilter, priceMin, priceMax, sortBy, sortDir]);

    const toggleSort = (field) => {
        if (sortBy === field) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortDir('desc');
        }
    };

    const clearAdvancedFilters = () => {
        setTypeFilter('all');
        setCityFilter('');
        setPriceMin('');
        setPriceMax('');
    };

    const basePath = isAdmin ? '/admin' : '/agent';

    const handleCardClick = (e, mandateId) => {
        // On desktop (lg+), open preview panel. On mobile, navigate.
        if (window.innerWidth >= 1024) {
            e.preventDefault();
            setPreviewId(mandateId);
        }
    };

    const SortIcon = ({ field }) => {
        if (sortBy !== field) return <ArrowUpDown className="w-3 h-3 text-white/20" />;
        return sortDir === 'asc' ? <ChevronUp className="w-3 h-3 text-[#FF914D]" /> : <ChevronDown className="w-3 h-3 text-[#FF914D]" />;
    };

    if (loading) {
        return (
            <div className="space-y-4" data-testid="mandates-page-loading">
                <Skeleton className="h-10 w-full bg-muted rounded-xl" />
                <div className="flex gap-2 overflow-hidden">
                    {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-8 w-20 bg-muted rounded-full flex-shrink-0" />)}
                </div>
                <div className="space-y-3">
                    {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-20 w-full bg-muted rounded-xl" />)}
                </div>
            </div>
        );
    }

    return (
        <motion.div className="space-y-4 sm:space-y-5" data-testid="mandates-page" initial="hidden" animate="visible" variants={stagger}>
            {/* Header */}
            <motion.div variants={fadeIn} className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <h1 className="text-xl sm:text-3xl font-black text-foreground truncate">
                        {isAdmin ? 'Mandats' : 'Mes mandats'}
                    </h1>
                    <p className="text-xs sm:text-sm text-muted-foreground flex items-center gap-2 flex-wrap">
                        <span>
                            {filteredMandates.length} mandat{filteredMandates.length > 1 ? 's' : ''}
                            {advancedFilterCount > 0 && <span className="text-[#FF914D]"> ({advancedFilterCount} filtre{advancedFilterCount > 1 ? 's' : ''})</span>}
                        </span>
                        <AnimatePresence>
                            {showSyncBadge && (
                                <motion.span
                                    key="sync-badge"
                                    initial={{ opacity: 0, y: -4, scale: 0.9 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0, y: -4, scale: 0.9 }}
                                    className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 px-2 py-0.5 text-[11px] font-medium"
                                    data-testid="mandates-sync-badge"
                                >
                                    <CheckCircle className="w-3 h-3" /> Liste synchronisée
                                </motion.span>
                            )}
                        </AnimatePresence>
                    </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => setSearchOpen(!searchOpen)}
                        className="md:hidden text-muted-foreground hover:text-white w-9 h-9" data-testid="toggle-search-button">
                        <Search className="w-4 h-4" />
                    </Button>
                    {/* Advanced filters toggle - desktop only */}
                    <Button variant="outline" size="sm" onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                        className={`hidden lg:flex items-center gap-1.5 border-border text-xs h-9 ${showAdvancedFilters || advancedFilterCount > 0 ? 'border-[#FF914D]/50 text-[#FF914D]' : 'text-muted-foreground hover:text-foreground'}`}
                        data-testid="toggle-advanced-filters">
                        <SlidersHorizontal className="w-3.5 h-3.5" />
                        Filtres
                        {advancedFilterCount > 0 && (
                            <span className="bg-[#FF914D] text-white rounded-full w-4 h-4 text-[10px] flex items-center justify-center">{advancedFilterCount}</span>
                        )}
                    </Button>
                    {isAdmin && (
                        <Button variant="outline" size="icon"
                            className="border-border text-muted-foreground hover:text-foreground hover:bg-card w-9 h-9 sm:w-auto sm:h-auto sm:px-3 sm:py-2"
                            data-testid="export-mandates-button">
                            <Download className="w-4 h-4 sm:mr-2" />
                            <span className="hidden sm:inline text-sm">Exporter</span>
                        </Button>
                    )}
                    <Link to={`${basePath}/mandates/upload`}>
                        <Button className="bg-gradient-to-r from-orange-500 to-red-500 hover:opacity-90 text-white shadow-lg shadow-orange-500/30 text-sm h-9 px-3 sm:px-4"
                            data-testid="upload-mandate-button">
                            <Upload className="w-4 h-4 sm:mr-1.5" />
                            <span className="hidden sm:inline">Uploader un mandat</span>
                        </Button>
                    </Link>
                </div>
            </motion.div>

            {/* Search bar */}
            <motion.div variants={fadeIn} className={`${searchOpen ? 'block' : 'hidden'} md:block`}>
                <div className="relative">
                    <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input placeholder="Rechercher par num., vendeur, ville, adresse..." value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10 h-10 bg-card border-border text-foreground placeholder:text-muted-foreground rounded-xl text-sm"
                        data-testid="mandates-search-input" />
                </div>
            </motion.div>

            {/* Status Filter Chips — Sticky on scroll */}
            <motion.div variants={fadeIn} className="sticky top-[57px] z-30 py-2 bg-background/90 backdrop-blur-lg md:static md:py-0 md:bg-transparent md:backdrop-blur-none">
                <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide -mx-1 px-1" data-testid="status-filter-chips">
                    {Object.entries(STATUS_CONFIG).map(([value, config]) => {
                        const count = statusCounts[value] || 0;
                        const isActive = statusFilter === value;
                        return (
                            <button key={value} onClick={() => setStatusFilter(value)}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all flex-shrink-0 ${
                                    isActive ? 'bg-gradient-to-r from-[#681793] to-[#FF914D] text-white shadow-lg shadow-[#681793]/20' 
                                    : 'bg-card border border-border text-muted-foreground hover:text-white hover:border-white/20'
                                }`} data-testid={`filter-chip-${value}`}>
                                <config.icon className="w-3 h-3" />
                                <span>{config.shortLabel}</span>
                                {count > 0 && (
                                    <span className={`ml-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${isActive ? 'bg-white/20' : 'bg-white/5'}`}>{count}</span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </motion.div>

            {/* Advanced Filters Panel (desktop) */}
            <AnimatePresence>
                {showAdvancedFilters && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                    >
                        <div className="bg-card rounded-xl border border-border p-4" data-testid="advanced-filters-panel">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                                    <Filter className="w-4 h-4 text-[#FF914D]" />
                                    Filtres avancés
                                </h3>
                                {advancedFilterCount > 0 && (
                                    <button onClick={clearAdvancedFilters} className="text-xs text-[#FF914D] hover:underline flex items-center gap-1">
                                        <X className="w-3 h-3" /> Réinitialiser
                                    </button>
                                )}
                            </div>
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                                {/* Type filter */}
                                <div>
                                    <label className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 block">Type</label>
                                    <div className="flex gap-1.5 flex-wrap">
                                        {['all', 'exclusif', 'semi_exclusif', 'simple'].map(t => (
                                            <button key={t} onClick={() => setTypeFilter(t)}
                                                className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${
                                                    typeFilter === t ? 'bg-[#FF914D]/20 text-[#FF914D] border border-[#FF914D]/30' : 'bg-white/5 text-muted-foreground border border-transparent hover:text-white'
                                                }`} data-testid={`type-filter-${t}`}>
                                                {t === 'all' ? 'Tous' : TYPE_LABELS[t]}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                
                                {/* City filter */}
                                <div>
                                    <label className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 block">Ville</label>
                                    <select value={cityFilter} onChange={(e) => setCityFilter(e.target.value)}
                                        className="w-full bg-white/5 border border-border rounded-lg px-2.5 py-1.5 text-xs text-white"
                                        data-testid="city-filter">
                                        <option value="" className="bg-slate-900">Toutes</option>
                                        {cities.map(c => <option key={c} value={c} className="bg-slate-900">{c}</option>)}
                                    </select>
                                </div>
                                
                                {/* Price range */}
                                <div>
                                    <label className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 block">Prix min</label>
                                    <Input type="number" value={priceMin} onChange={(e) => setPriceMin(e.target.value)}
                                        placeholder="0 €" className="h-8 text-xs bg-white/5 border-border" data-testid="price-min-filter" />
                                </div>
                                <div>
                                    <label className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 block">Prix max</label>
                                    <Input type="number" value={priceMax} onChange={(e) => setPriceMax(e.target.value)}
                                        placeholder="999 999 €" className="h-8 text-xs bg-white/5 border-border" data-testid="price-max-filter" />
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Mandates list */}
            {filteredMandates.length === 0 ? (
                <motion.div variants={fadeIn}>
                    <div className="bg-card backdrop-blur-sm rounded-2xl border border-border p-8 sm:p-12 text-center">
                        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-white/5 flex items-center justify-center mx-auto mb-4">
                            <FileText className="w-8 h-8 sm:w-10 sm:h-10 text-muted-foreground" />
                        </div>
                        <h3 className="text-lg sm:text-xl font-semibold text-foreground mb-2">Aucun mandat</h3>
                        <p className="text-sm text-muted-foreground mb-6">
                            {search || statusFilter !== 'all' || advancedFilterCount > 0
                                ? 'Aucun mandat ne correspond à vos critères.'
                                : "Vous n'avez pas encore de mandat."}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-2 justify-center">
                            <Link to={`${basePath}/mandates/upload`}>
                                <Button className="bg-gradient-to-r from-orange-500 to-red-500 text-white">
                                    <Upload className="w-4 h-4 mr-2" /> Uploader un mandat
                                </Button>
                            </Link>
                        </div>
                    </div>
                </motion.div>
            ) : (
                <>
                    {/* Desktop Table Header - hidden on mobile */}
                    <div className="hidden lg:block" data-testid="mandates-table-header">
                        <div className="bg-card/50 rounded-xl border border-border px-5 py-2.5">
                            <div className="grid grid-cols-12 gap-4 items-center text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                                <button onClick={() => toggleSort('mandate_number')} className="col-span-2 flex items-center gap-1 hover:text-white transition-colors text-left">
                                    N° Mandat <SortIcon field="mandate_number" />
                                </button>
                                <button onClick={() => toggleSort('type')} className="col-span-1 flex items-center gap-1 hover:text-white transition-colors text-left">
                                    Type <SortIcon field="type" />
                                </button>
                                <div className="col-span-2">Vendeur</div>
                                <button onClick={() => toggleSort('city')} className="col-span-2 flex items-center gap-1 hover:text-white transition-colors text-left">
                                    <MapPin className="w-3 h-3" /> Ville <SortIcon field="city" />
                                </button>
                                <button onClick={() => toggleSort('price')} className="col-span-2 flex items-center gap-1 hover:text-white transition-colors text-left justify-end">
                                    Prix <SortIcon field="price" />
                                </button>
                                <button onClick={() => toggleSort('status')} className="col-span-1 flex items-center gap-1 hover:text-white transition-colors text-left">
                                    Statut <SortIcon field="status" />
                                </button>
                                <button onClick={() => toggleSort('created_at')} className="col-span-2 flex items-center gap-1 hover:text-white transition-colors text-left justify-end">
                                    Date <SortIcon field="created_at" />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* List */}
                    <motion.div variants={stagger} className="space-y-1.5 sm:space-y-2">
                        {filteredMandates.map((mandate) => {
                            const sConfig = STATUS_CONFIG[mandate.status] || STATUS_CONFIG.draft;
                            const StatusIcon = sConfig.icon;
                            const salePrice = mandate.pricing?.sale_price || 0;
                            const sellerNames = mandate.sellers?.map(s => `${s.first_name} ${s.last_name}`).join(', ') || '';
                            const city = mandate.property?.city || '';
                            const typeColor = TYPE_COLORS[mandate.mandate_type] || TYPE_COLORS.simple;
                            const isSelected = previewId === mandate.id;

                            return (
                                <motion.div key={mandate.id} variants={fadeIn}>
                                    <Link to={`${basePath}/mandates/v2/${mandate.id}`}
                                        onClick={(e) => handleCardClick(e, mandate.id)}
                                        data-testid={`mandate-card-${mandate.id}`}>
                                        
                                        {/* Mobile card */}
                                        <div className={`lg:hidden bg-card backdrop-blur-sm rounded-xl border hover:border-white/15 active:scale-[0.99] transition-all duration-200 p-3.5 group ${isSelected ? 'border-[#FF914D]/50 bg-[#FF914D]/5' : 'border-border'}`}>
                                            <div className="flex items-start gap-3">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center justify-between gap-2 mb-1">
                                                        <div className="flex items-center gap-2 min-w-0">
                                                            <span className="font-semibold text-sm text-foreground truncate">{mandate.mandate_number}</span>
                                                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${typeColor}`}>
                                                                {TYPE_LABELS[mandate.mandate_type] || mandate.mandate_type}
                                                            </span>
                                                        </div>
                                                        <span className="font-bold text-sm text-white whitespace-nowrap">{formatCurrencyFull(salePrice)}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1.5">
                                                        {sellerNames && <span className="truncate max-w-[140px]">{sellerNames}</span>}
                                                        {city && <span className="flex items-center gap-0.5 shrink-0"><MapPin className="w-3 h-3" />{city}</span>}
                                                    </div>
                                                    {mandate.agent_name && isAdmin && (
                                                        <p className="text-[10px] text-muted-foreground/70 mb-1.5 truncate">Agent : {mandate.agent_name}</p>
                                                    )}
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-2.5">
                                                            <Badge className={`${sConfig.color} border-0 flex items-center gap-1 text-[11px] px-2 py-0.5`}>
                                                                <StatusIcon className="w-3 h-3" />{sConfig.label}
                                                            </Badge>
                                                            <MandateTimelineCompact status={mandate.status} />
                                                        </div>
                                                        <div className="flex items-center gap-1.5">
                                                            <span className="text-[10px] text-muted-foreground/50">{formatDate(mandate.created_at)}</span>
                                                            <ChevronRight className="w-4 h-4 text-white/20 group-hover:text-white/50 transition-colors" />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Desktop table row */}
                                        <div className={`hidden lg:block rounded-xl border hover:border-white/15 transition-all duration-200 px-5 py-3 group cursor-pointer ${
                                            isSelected ? 'border-[#FF914D]/50 bg-[#FF914D]/5' : 'border-border bg-card hover:bg-card/80'
                                        }`}>
                                            <div className="grid grid-cols-12 gap-4 items-center">
                                                {/* Number */}
                                                <div className="col-span-2 flex items-center gap-3">
                                                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#681793]/20 to-[#FF914D]/20 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                                                        <FileText className="w-4 h-4 text-[#681793]" />
                                                    </div>
                                                    <span className="font-semibold text-sm text-foreground truncate">{mandate.mandate_number}</span>
                                                </div>
                                                
                                                {/* Type */}
                                                <div className="col-span-1">
                                                    <span className={`px-2 py-0.5 rounded text-[10px] font-medium border ${typeColor}`}>
                                                        {TYPE_LABELS[mandate.mandate_type] || mandate.mandate_type}
                                                    </span>
                                                </div>
                                                
                                                {/* Seller */}
                                                <div className="col-span-2 text-sm text-muted-foreground truncate">{sellerNames || '-'}</div>
                                                
                                                {/* City */}
                                                <div className="col-span-2 text-sm text-muted-foreground flex items-center gap-1 truncate">
                                                    {city && <><MapPin className="w-3 h-3 shrink-0" />{city}</>}
                                                </div>
                                                
                                                {/* Price */}
                                                <div className="col-span-2 text-right">
                                                    <span className="font-bold text-sm text-white">{formatCurrencyFull(salePrice)}</span>
                                                    {mandate.pricing?.commission_rate && (
                                                        <p className="text-[10px] text-muted-foreground">{mandate.pricing.commission_rate}% comm.</p>
                                                    )}
                                                </div>
                                                
                                                {/* Status */}
                                                <div className="col-span-1">
                                                    <Badge className={`${sConfig.color} border-0 flex items-center gap-1 text-[10px] px-1.5 py-0.5 w-fit`}>
                                                        <StatusIcon className="w-3 h-3" />{sConfig.shortLabel}
                                                    </Badge>
                                                </div>
                                                
                                                {/* Date + Arrow */}
                                                <div className="col-span-2 flex items-center justify-end gap-2">
                                                    <span className="text-xs text-muted-foreground">{formatDateLong(mandate.created_at)}</span>
                                                    <ChevronRight className="w-4 h-4 text-white/15 group-hover:text-white/40 transition-colors" />
                                                </div>
                                            </div>
                                        </div>
                                    </Link>
                                </motion.div>
                            );
                        })}
                    </motion.div>
                </>
            )}

            {/* Preview Panel (desktop only) */}
            <MandatePreviewPanel
                mandateId={previewId}
                onClose={() => setPreviewId(null)}
                basePath={basePath}
            />
        </motion.div>
    );
}

export default MandatesPage;
